//#region IMPORTS
import {
  BILLING_ADDRESS_CODE,
  DELIVERY_ADDRESS_CODE,
} from "../../models/_consts";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { StoreDetail } from "../../models/store";
import { Tooltip } from "react-tooltip";
import StoreDetailsAddressList from "./StoreDetailsAddressList";
//#endregion

//#region INTERFACE
interface Props {
  store: StoreDetail;
}
//#endregion

export default function StoreDetailsContainer({ store }: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();
  //#endregion

  //#region RENDER
  return (
    <section>
      <div className="space-y-12">
        {/* INFO SECTION */}
        <section className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
          <header>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {t("rituals.storeInformation")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {t("rituals.theStoresKeyDetailsAndAdditionalRelevantInformation")}
            </p>
          </header>

          <article className="grid max-w-6xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.storeName")}
              </p>

              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.store_name || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.storeId")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.external_store_id || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.billingId")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.billing_id || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.storeFormat")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.store_format
                    ? store?.store_format.name
                    : "No storeformat found. Please ask to edit this store."}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.language")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.language || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.digitalFrame")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.digital_frame_name || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-6">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.comment")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.comment || "/"}
                </p>
              </div>
            </div>
          </article>
        </section>

        {/* CONTACT PERSON SECTION */}
        <section className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
          <header>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {t("rituals.contactInformation")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {t("rituals.theStoresContactInformation")}
            </p>
          </header>

          <article className="grid max-w-6xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.contactFirstName")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.contact_first_name || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.contactLastName")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.contact_last_name || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.contactEmail")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.contact_email || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.contactPhone")}
              </p>
              <div className="mt-2">
                <p className="text-slate-600 font-medium">
                  {store?.contact_phone || "/"}
                </p>
              </div>
            </div>
          </article>
        </section>

        {/* SETTINGS SECTION */}
        <section className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
          <header>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {t("rituals.storeSettings")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {t(
                "rituals.provideConfigurationDetailsAndPreferencesForTheStoreSettings"
              )}
            </p>
          </header>

          <article className="grid max-w-6xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
            <div className="sm:col-span-3">
              <p className="block text-sm font-medium leading-6 text-gray-900">
                {t("rituals.openingDate")}
              </p>
              <div className="mt-2">
                <p className="text-slate-900 font-medium">
                  {store?.opening_date || "/"}
                </p>
              </div>
            </div>

            <div className="sm:col-span-3">
              <div className="mt-2 space-y-6">
                <div
                  data-tooltip-variant="error"
                  data-tooltip-place="left"
                  data-tooltip-content={
                    store.active ? null : t("rituals.thisStoreIsNoLongerActive")
                  }
                  data-tooltip-id={"store-disabled-tooltip" + store.id}
                  className="relative flex items-center gap-x-3"
                >
                  {store.active ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-400" />
                  )}
                  <p className="text-sm font-medium text-gray-900">
                    {t(
                      store.active
                        ? "rituals.activeStore"
                        : "rituals.disabledStore"
                    )}
                  </p>
                  <Tooltip id={"store-disabled-tooltip" + store.id} />
                </div>
              </div>
            </div>
          </article>
        </section>

        {/* ADDRESS SECTION */}
        <section className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
          <header>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              {t("rituals.addressList")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {t("rituals.detailsAndKeyInformationForTheAddressList")}
            </p>
          </header>
          <article className="grid max-w-6xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
            <StoreDetailsAddressList
              type={DELIVERY_ADDRESS_CODE}
              addresses={store.delivery_addresses.filter(
                (a) => a.type === DELIVERY_ADDRESS_CODE
              )}
            />
            <StoreDetailsAddressList
              type={BILLING_ADDRESS_CODE}
              addresses={store.delivery_addresses.filter(
                (a) => a.type === BILLING_ADDRESS_CODE
              )}
            />
          </article>
        </section>
      </div>
    </section>
  );
  //#endregion
}
